/**
 * Step-up fallback minters (STEPUP_FALLBACK_DESIGN.md §6b/§7). When Face ID is unavailable or fails,
 * these obtain a single-use grant for a sensitive operation via a server-verified factor:
 *   • app-PIN  — the device PIN (same secret as the app lock), for REVEAL only;
 *   • email-OTP — an emailed code (confirm-only, no session), for any operation.
 * The grant is passed back on the sensitive call in the X-Reveal-Grant header.
 */
import { http } from './http';

export type StepUpPurpose = 'REVEAL' | 'PIN_CHANGE' | 'CARD_BLOCK' | 'CARD_UNFREEZE' | 'MASTER_KEY_EXPORT'
  | 'MEMBER_TOP_UP'
  /** Vouching for a family member who lost access (docs/cabinet-redesign/04 §0.2 З-7) — support is the side asking. */
  | 'IDENTITY_VOUCH';

/**
 * Whether an operator's support code may stand in for the client's own factor. The export of a master wallet's key
 * and a member's card top-up (money leaves the shared balance, docs/cabinet-redesign/07) refuse it (the server answers
 * STEP_UP_REQUIRED again), so the chooser does not offer it there. Both still take every factor the client holds —
 * passkey, app PIN, e-mail code (the key export: owner 31.10, docs/cabinet-redesign/04 §0.2 З-5 — a PIN / passkey only
 * once enrolled 3 days ago); the chooser shows whichever of them the server's 403 lists and this device has.
 */
export function supportCodeAccepted(purpose: StepUpPurpose): boolean {
  return purpose !== 'MASTER_KEY_EXPORT' && purpose !== 'MEMBER_TOP_UP' && purpose !== 'IDENTITY_VOUCH';
}
/**
 * The support code's symbols — Crockford's base32, as the server mints it: no I, L, O, U, so nothing is misheard over the
 * phone. The code is 8 of them, shown and typed as XXXX-XXXX; single use, 15 minutes, 5 tries.
 */
const SUPPORT_CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const SUPPORT_CODE_LENGTH = 8;

/** What the client typed, shaped into the code: upper case, O → 0, I/L → 1, other symbols dropped, grouped 4-4. */
export function formatSupportCode(typed: string): string {
  const symbols = typed.toUpperCase().replace(/O/g, '0').replace(/[IL]/g, '1')
    .split('').filter((c) => SUPPORT_CODE_ALPHABET.includes(c)).slice(0, SUPPORT_CODE_LENGTH).join('');
  return symbols.length > 4 ? `${symbols.slice(0, 4)}-${symbols.slice(4)}` : symbols;
}

/** All 8 symbols are there. */
export function supportCodeComplete(formatted: string): boolean {
  return formatted.replace('-', '').length === SUPPORT_CODE_LENGTH;
}

export type StepUpFactor = 'PASSKEY' | 'APP_PIN' | 'EMAIL_OTP';

/**
 * A factor of this device the 403 STEP_UP_REQUIRED does not offer yet, and from when it will (`availableAt`, ISO). Only
 * the key export has them: an app PIN or a passkey counts there once enrolled at least 3 days ago (owner 31.10,
 * docs/cabinet-redesign/04 §0.2 З-5); the e-mail code always does.
 */
export interface PendingStepUpFactor {
  factor: StepUpFactor;
  availableAt: string;
}

/** Uniform minter response — grant is non-null only when ok. */
export interface StepUpGrant {
  ok: boolean;
  status: string;
  grant: string | null;
}

export const stepUpApi = {
  /** Verify the device PIN → grant. Uniform response (no set/mismatch/locked oracle). */
  appPin(pin: string, purpose: StepUpPurpose) {
    return http
      .post<StepUpGrant>('/v1/showcase/me/stepup/app-pin', { pin, purpose })
      .then(r => r.data);
  },

  /** Send a step-up OTP to the registered email. Confirm-only — never issues a session. */
  otpRequest() {
    return http
      .post<{ requestId: string }>('/v1/showcase/me/stepup/otp/request')
      .then(r => r.data);
  },

  /** Verify the emailed code → grant. */
  otpVerify(requestId: string, code: string, purpose: StepUpPurpose) {
    return http
      .post<StepUpGrant>('/v1/showcase/me/stepup/otp/verify', { requestId, code, purpose })
      .then(r => r.data);
  },
};
